import { type ReactNode, useEffect, useRef, useState } from "react";
import { Dimensions, StyleSheet, Text, View } from "react-native";
import { AnimatePresence, MotiView } from "moti";
import { Portal } from "react-native-paper";

import { colors, radii, spacing } from "../../../styles/design-system";

const ESTIMATED_TOOLTIP_HEIGHT = 24;
const ESTIMATED_TOOLTIP_WIDTH = 82;
const TOOLTIP_DELAY_MS = 200;

interface TooltipPosition {
  left: number;
  top: number;
}

interface TooltipAnchorLayout {
  height: number;
  pageX: number;
  pageY: number;
  width: number;
}

export interface ChatTooltipProps {
  children: ReactNode;
  content: string;
}

export const ChatTooltip = ({ children, content }: ChatTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [tooltipSize, setTooltipSize] = useState({ height: ESTIMATED_TOOLTIP_HEIGHT, width: ESTIMATED_TOOLTIP_WIDTH });
  const [anchorLayout, setAnchorLayout] = useState<TooltipAnchorLayout | null>(null);
  const targetRef = useRef<View>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideTooltip = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    setVisible(false);
  };

  const getTooltipPosition = (layout: TooltipAnchorLayout, size = tooltipSize): TooltipPosition => {
    const screenWidth = Dimensions.get("window").width;
    const tooltipTop = layout.pageY + layout.height + 6;
    let tooltipLeft = layout.pageX + layout.width / 2 - size.width / 2;

    if (tooltipLeft + size.width > screenWidth) {
      tooltipLeft = screenWidth - size.width - 10;
    }

    if (tooltipLeft < 10) {
      tooltipLeft = 10;
    }

    return { top: tooltipTop, left: tooltipLeft };
  };

  const showTooltip = () => {
    const targetNode = targetRef.current;

    if (!targetNode) {
      return;
    }

    targetNode.measure((x, y, width, height, pageX, pageY) => {
      const layout = { width, height, pageX, pageY };
      setAnchorLayout(layout);
      setTooltipPosition(getTooltipPosition(layout));
      setVisible(true);
    });
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(showTooltip, TOOLTIP_DELAY_MS);
  };

  useEffect(() => {
    if (visible && anchorLayout) {
      setTooltipPosition(getTooltipPosition(anchorLayout));
    }
  }, [anchorLayout, tooltipSize, visible]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <View>
      <View
        ref={targetRef}
        // @ts-expect-error - supported by react-native-web
        onMouseEnter={handleMouseEnter}
        // @ts-expect-error - supported by react-native-web
        onMouseLeave={hideTooltip}
        onTouchStart={hideTooltip}>
        {children}
      </View>
      <Portal>
        <AnimatePresence>
          {visible && (
            <MotiView
              key={content}
              from={{ opacity: 0, translateY: -6, scale: 0.96 }}
              animate={{ opacity: 1, translateY: 0, scale: 1 }}
              exit={{ opacity: 0, translateY: -4, scale: 0.96 }}
              transition={{ type: "timing", duration: 160 }}
              pointerEvents="none"
              style={[styles.tooltipContainer, tooltipPosition]}>
              <View
                onLayout={(event) => {
                  const { height, width } = event.nativeEvent.layout;

                  if (tooltipSize.height !== height || tooltipSize.width !== width) {
                    setTooltipSize({ height, width });
                  }
                }}
                style={styles.tooltip}>
                <Text style={styles.tooltipText}>{content}</Text>
              </View>
            </MotiView>
          )}
        </AnimatePresence>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  tooltipContainer: {
    position: "absolute",
    zIndex: 1000,
    alignItems: "flex-start",
  },
  tooltip: {
    backgroundColor: colors.brand[900],
    borderRadius: radii.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: 3,
    shadowColor: colors.brand[950],
    shadowOpacity: 0.14,
    shadowRadius: 10,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    elevation: 4,
  },
  tooltipText: {
    color: colors.white,
    fontSize: 11,
    lineHeight: 16,
    fontWeight: "500",
  },
});
